import { Radio } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Alert, Button, EmptyState, Spinner } from '@/components/ui'

import { useChannels } from '../queries'
import { ChannelCard } from './ChannelCard'

export interface ChannelsListProps {
  readonly tenantId: string
}

/**
 * Lista os canais do tenant em cards. Estados: carregando (Spinner), erro (Alert + tentar de novo),
 * vazio (EmptyState com acao — empty state = action state) e a lista de cards. O canal WhatsApp e
 * conectado na configuracao inicial; se o usuario acabou de comecar, atualizar traz o canal recem
 * criado sem recarregar a pagina.
 */
export function ChannelsList({ tenantId }: ChannelsListProps) {
  const { t } = useTranslation()
  const channelsQuery = useChannels(tenantId)

  if (channelsQuery.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label={t('channels.loading')} />
      </div>
    )
  }

  if (channelsQuery.isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Alert variant="danger" title={t('channels.loadError')} />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void channelsQuery.refetch()
          }}
        >
          {t('retry')}
        </Button>
      </div>
    )
  }

  if (channelsQuery.data.length === 0) {
    return (
      <EmptyState
        icon={Radio}
        title={t('channels.empty.title')}
        description={t('channels.empty.description')}
        action={
          <Button
            variant="secondary"
            loading={channelsQuery.isRefetching}
            onClick={() => {
              void channelsQuery.refetch()
            }}
          >
            {t('channels.empty.refresh')}
          </Button>
        }
      />
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {channelsQuery.data.map((channel) => (
        <ChannelCard key={channel.id} tenantId={tenantId} channel={channel} />
      ))}
    </div>
  )
}
